import gsap from "gsap"
import { activeMultiplier } from "./animationSpeed"
import { getBezierPoint } from "./animationHelpers"
import { playPieceMovementSound } from "./audioManager"

/**
 * "Crossing arcs" animation for the "swap position with any player" card: the
 * two pieces lift off their cells, fly along mirrored bezier arcs over the
 * board (visibly crossing each other with a flash at the midpoint), and land
 * with a squash-and-stretch bounce + shockwave ring on their new cells.
 *
 * The store applies the swapped positions instantly (React re-renders the
 * pieces straight into their new cells), so the effect is pure presentation:
 * the REAL pieces are hidden for the duration via body-level CSS classes
 * (immune to any mid-flight re-render recreating the <img> elements) while
 * CLONES animate inside a throwaway pointer-events-none overlay on <body> in
 * plain viewport coordinates — which also sidesteps the per-side board
 * rotation that makes transforming the real pieces painful.
 *
 * Usage (two-phase, because the origin rects must be captured BEFORE the store
 * update re-renders the pieces into their new cells):
 *
 *   const anim = prepareSwapPositionsAnimation(orderA, orderB) // pre-dispatch
 *   dispatch(...apply swapped situation...)
 *   anim?.play().finally(...)                                  // post-dispatch
 */

// Neutral "swap magic" palette — distinct from the striking beam's danger
// amber/crimson so the exchange reads as relocation, not another attack.
const CORE = "#ffffff"
const VIOLET = "#8b7bff"
const CYAN = "#7fd8ff"

const HIDE_STYLE_ID = "swap-anim-hide-styles"
const hideClassFor = (order: number) => `swap-anim-hide-piece-${order}`

// One <style> with a body-class rule per player order. Hiding through a body
// class (instead of inline styles) keeps the real pieces hidden even if a
// concurrent situation update re-creates their <img> elements mid-animation.
// `#piece-fx-<order>` is the turn/own-player highlight around the token
// (Components/Pieces.tsx) — it has to go with it, or the halo stays behind in
// the old cell while the clone flies.
const ensureHideStyles = () => {
  if (document.getElementById(HIDE_STYLE_ID)) return
  const style = document.createElement("style")
  style.id = HIDE_STYLE_ID
  style.textContent = Array.from(
    { length: 6 },
    (_, o) =>
      `body.${hideClassFor(o)} #piece-${o}, body.${hideClassFor(o)} #piece-fx-${o} { visibility: hidden !important; }`
  ).join("\n")
  document.head.appendChild(style)
}

interface IPoint {
  x: number
  y: number
}

const centerOf = (rect: DOMRect): IPoint => ({
  x: rect.x + rect.width / 2,
  y: rect.y + rect.height / 2,
})

const makeEl = (parent: HTMLElement, styles: Partial<CSSStyleDeclaration>): HTMLDivElement => {
  const el = document.createElement("div")
  el.style.position = "absolute"
  el.style.pointerEvents = "none"
  el.style.willChange = "transform, opacity"
  Object.assign(el.style, styles)
  parent.appendChild(el)
  return el
}

// A clone of a piece image, centered on `at` via xPercent/yPercent so left/top
// updates during flight never fight the scale tweens.
const makePieceClone = (parent: HTMLElement, src: string, at: IPoint, size: IPoint): HTMLImageElement => {
  const img = document.createElement("img")
  img.src = src
  Object.assign(img.style, {
    position: "absolute",
    left: `${at.x}px`,
    top: `${at.y}px`,
    width: `${size.x}px`,
    height: `${size.y}px`,
    pointerEvents: "none",
    willChange: "transform, left, top",
  } as Partial<CSSStyleDeclaration>)
  parent.appendChild(img)
  gsap.set(img, { xPercent: -50, yPercent: -50 })
  return img
}

// Expanding shockwave ring, used on lift-off and landing.
const spawnRing = (parent: HTMLElement, at: IPoint, color: string, startSize: number, atTime: number, tl: gsap.core.Timeline) => {
  const ring = makeEl(parent, {
    left: `${at.x}px`,
    top: `${at.y}px`,
    width: `${startSize}px`,
    height: `${startSize}px`,
    borderRadius: "50%",
    border: `3px solid ${color}`,
    boxShadow: `0 0 14px 2px ${color}80`,
    opacity: "0",
  })
  gsap.set(ring, { xPercent: -50, yPercent: -50, scale: 0.4 })
  tl.set(ring, { opacity: 0.9 }, atTime).to(
    ring,
    { scale: 2.8, opacity: 0, duration: 0.45 * activeMultiplier(), ease: "power2.out" },
    atTime
  )
}

interface ITraveler {
  clone: HTMLImageElement
  ghosts: HTMLImageElement[]
  from: IPoint
  to: IPoint
  control: IPoint
  fromSize: IPoint
  toSize: IPoint
}

export interface ISwapPositionsAnimation {
  /** Measure the re-rendered destination pieces and run the full effect. */
  play: () => Promise<void>
}

export const prepareSwapPositionsAnimation = (
  orderA: PlayerOrder,
  orderB: PlayerOrder
): ISwapPositionsAnimation | null => {
  const elA = document.getElementById(`piece-${orderA}`) as HTMLImageElement | null
  const elB = document.getElementById(`piece-${orderB}`) as HTMLImageElement | null
  if (!elA || !elB) return null
  const rectA = elA.getBoundingClientRect()
  const rectB = elB.getBoundingClientRect()
  if (!rectA.width || !rectB.width) return null

  ensureHideStyles()
  const hideClasses = [hideClassFor(orderA), hideClassFor(orderB)]
  document.body.classList.add(...hideClasses)

  // Full-screen overlay hosting the whole effect (same pattern as the striking
  // beam) — the clones take over the exact spots the real pieces vacated.
  const container = document.createElement("div")
  Object.assign(container.style, {
    position: "fixed",
    inset: "0",
    zIndex: "130",
    pointerEvents: "none",
    overflow: "hidden",
  } as Partial<CSSStyleDeclaration>)
  document.body.appendChild(container)

  const mult = activeMultiplier()
  const ghostLags = [0.09, 0.18, 0.27] // trail followers, as a fraction of flight progress

  const buildTraveler = (src: string, from: DOMRect, to: DOMRect, bowSign: 1 | -1): ITraveler => {
    const fromC = centerOf(from)
    const toC = centerOf(to)
    // Control point offset perpendicular to the travel line so the two arcs bow
    // in opposite directions and the pieces visibly cross mid-board.
    const dx = toC.x - fromC.x
    const dy = toC.y - fromC.y
    const dist = Math.hypot(dx, dy) || 1
    const bow = Math.min(Math.max(dist * 0.25, 60), 220) * bowSign
    const control = {
      x: (fromC.x + toC.x) / 2 + (-dy / dist) * bow,
      y: (fromC.y + toC.y) / 2 + (dx / dist) * bow,
    }
    const ghosts = ghostLags.map((_, i) => {
      const ghost = makePieceClone(container, src, fromC, { x: from.width, y: from.height })
      ghost.style.opacity = "0"
      ghost.style.filter = "blur(1px)"
      gsap.set(ghost, { xPercent: -50, yPercent: -50, scale: 0.9 - i * 0.12 })
      return ghost
    })
    // The main clone is created after its ghosts so it paints on top of them.
    const clone = makePieceClone(container, src, fromC, { x: from.width, y: from.height })
    return {
      clone,
      ghosts,
      from: fromC,
      to: toC,
      control,
      fromSize: { x: from.width, y: from.height },
      toSize: { x: to.width, y: to.height },
    }
  }

  let settled = false
  let resolveDone: () => void = () => {}
  const done = new Promise<void>((res) => (resolveDone = res))
  const cleanup = () => {
    if (settled) return
    settled = true
    document.body.classList.remove(...hideClasses)
    container.remove()
    resolveDone()
  }
  // Safety net: never leave the real pieces hidden if GSAP gets throttled in a
  // backgrounded tab or an element disappears mid-flight.
  window.setTimeout(cleanup, 3000 * mult + 1000)

  const play = (): Promise<void> => {
    // Wait two frames so React has committed the swapped positions and the
    // (hidden) real pieces can be measured at their destination cells. rAF runs
    // before paint, so nothing ever flashes at the new position.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (settled) return
        const destA = document.getElementById(`piece-${orderA}`)?.getBoundingClientRect()
        const destB = document.getElementById(`piece-${orderB}`)?.getBoundingClientRect()
        if (!destA?.width || !destB?.width) {
          // A piece didn't survive the update (e.g. went bankrupt in the same
          // tick) — nothing sensible to animate, just reveal and bail.
          cleanup()
          return
        }

        const prefersReducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
        if (prefersReducedMotion) {
          // Minimal cue: reveal the pieces in place and pulse a ring on both
          // destination cells.
          document.body.classList.remove(...hideClasses)
          const tl = gsap.timeline({ onComplete: cleanup })
          spawnRing(container, centerOf(destA), VIOLET, Math.max(destA.width, 40), 0, tl)
          spawnRing(container, centerOf(destB), VIOLET, Math.max(destB.width, 40), 0, tl)
          playPieceMovementSound().catch(console.error)
          return
        }

        const travelers = [
          buildTraveler(elA.src, rectA, destA, 1),
          buildTraveler(elB.src, rectB, destB, -1),
        ]

        const LIFT = 0.18 * mult
        const FLIGHT = 0.62 * mult
        const LAND = 0.2 * mult
        const flightStart = LIFT
        const impactAt = flightStart + FLIGHT

        const tl = gsap.timeline({ onComplete: cleanup })

        travelers.forEach((tr) => {
          // Lift-off: pop up + glow ring left behind on the origin cell.
          spawnRing(container, tr.from, CYAN, Math.max(tr.fromSize.x, 40), 0, tl)
          tl.to(tr.clone, { scale: 1.35, duration: LIFT, ease: "back.out(2)" }, 0)

          // Flight along the arc. A single progress proxy drives the clone and
          // its trail ghosts (each sampling the same curve slightly behind), so
          // the whole train stays perfectly on-path.
          const proxy = { t: 0 }
          tl.to(
            proxy,
            {
              t: 1,
              duration: FLIGHT,
              ease: "power1.inOut",
              onUpdate: () => {
                const t = proxy.t
                const pos = getBezierPoint(tr.from.x, tr.from.y, tr.control.x, tr.control.y, tr.to.x, tr.to.y, t)
                tr.clone.style.left = `${pos.x}px`
                tr.clone.style.top = `${pos.y}px`
                // Pieces can render at a different size in the destination cell
                // (crowding) — morph the clone toward it during flight.
                tr.clone.style.width = `${tr.fromSize.x + (tr.toSize.x - tr.fromSize.x) * t}px`
                tr.clone.style.height = `${tr.fromSize.y + (tr.toSize.y - tr.fromSize.y) * t}px`
                tr.ghosts.forEach((ghost, i) => {
                  const gt = Math.max(t - ghostLags[i], 0)
                  const gpos = getBezierPoint(tr.from.x, tr.from.y, tr.control.x, tr.control.y, tr.to.x, tr.to.y, gt)
                  ghost.style.left = `${gpos.x}px`
                  ghost.style.top = `${gpos.y}px`
                  ghost.style.opacity = t > ghostLags[i] && t < 0.97 ? String(0.38 - i * 0.11) : "0"
                })
              },
            },
            flightStart
          )
          // Mid-flight scale swell so the piece reads as sailing "over" the board.
          tl.to(tr.clone, { scale: 1.6, duration: FLIGHT / 2, ease: "sine.out" }, flightStart).to(
            tr.clone,
            { scale: 1, duration: FLIGHT / 2, ease: "sine.in" },
            flightStart + FLIGHT / 2
          )

          // Landing: squash-and-stretch bounce + shockwave on the new cell.
          spawnRing(container, tr.to, VIOLET, Math.max(tr.toSize.x, 40), impactAt, tl)
          tl.to(tr.clone, { scaleX: 1.25, scaleY: 0.72, duration: 0.07 * mult, ease: "power2.in" }, impactAt).to(
            tr.clone,
            { scaleX: 1, scaleY: 1, duration: LAND - 0.07 * mult, ease: "back.out(3)" },
            impactAt + 0.07 * mult
          )
        })

        // The "swap!" beat: a flash where the two arcs cross mid-board.
        const mid = {
          x: (travelers[0].from.x + travelers[0].to.x) / 2,
          y: (travelers[0].from.y + travelers[0].to.y) / 2,
        }
        const crossFlash = makeEl(container, {
          left: `${mid.x}px`,
          top: `${mid.y}px`,
          width: "120px",
          height: "120px",
          borderRadius: "50%",
          background: `radial-gradient(circle, ${CORE} 0%, ${CYAN} 35%, ${VIOLET}00 70%)`,
          opacity: "0",
        })
        gsap.set(crossFlash, { xPercent: -50, yPercent: -50, scale: 0.3 })
        const crossAt = flightStart + FLIGHT / 2
        tl.set(crossFlash, { opacity: 0.95 }, crossAt).to(
          crossFlash,
          { scale: 1.7, opacity: 0, duration: 0.28 * mult, ease: "power2.out" },
          crossAt
        )
        spawnRing(container, mid, CORE, 50, crossAt, tl)

        // Whoosh on take-off, thump on landing.
        tl.call(
          () => {
            playPieceMovementSound().catch(console.error)
          },
          [],
          flightStart
        )
        tl.call(
          () => {
            playPieceMovementSound().catch(console.error)
          },
          [],
          impactAt
        )

        // Reveal the real pieces exactly under the settled clones, then drop
        // the clones a beat later so there is never a gap or a double-image pop.
        tl.call(() => document.body.classList.remove(...hideClasses), [], impactAt + LAND)
        travelers.forEach((tr) => {
          tl.to(tr.clone, { opacity: 0, duration: 0.1 * mult }, impactAt + LAND + 0.02 * mult)
        })
      })
    })
    return done
  }

  return { play }
}
